// Rounded-2xl chart card mirroring the iOS chart sections. Tapping the header
// chevron opens a bottom Sheet with a larger chart plus a data table —
// the web equivalent of the iOS expandable chart detail.
import { useState } from 'react';
import { Maximize2 } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';

export function NoData({ message, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[180px] rounded-xl border border-gray-100 bg-white text-gray-400 gap-2">
      {Icon && <Icon size={36} className="opacity-40" />}
      <p className="text-xs">{message}</p>
    </div>
  );
}

// detail: { chart: ReactNode, columns: [{key,label,align,money}], rows: [] }
export default function ChartCard({ title, subtitle = null, right = null, children, detail = null }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => detail && setOpen(true)}
          className={`flex-1 text-left ${detail ? '' : 'cursor-default'}`}
        >
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-gray-900">{title}</h3>
            {detail && <Maximize2 size={13} className="text-gray-300" />}
          </div>
          {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
        </button>
        {right}
      </div>

      <div className="mt-2">{children}</div>

      {detail && (
        <Sheet open={open} onClose={() => setOpen(false)} title={title} maxHeight="90vh">
          <div className="px-4 pb-6 space-y-4">
            {detail.chart}
            {detail.rows?.length > 0 && (
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-400">
                      {detail.columns.map((c) => (
                        <th key={c.key} className={`px-3 py-2 font-semibold ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.rows.map((r, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        {detail.columns.map((c) => (
                          <td key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right font-semibold text-gray-800' : 'text-gray-600'}`}>
                            {c.money ? `$${(Number(r[c.key]) || 0).toFixed(2)}` : r[c.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Sheet>
      )}
    </div>
  );
}
